import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { NIVILABS_LOGO ,REGISTER_IMG} from "@/lib/api";

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    confirmPassword: "",
    phone: "",
    organization: "",
    address: "",
    pincode: "",
    gst: "",
  });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {

      if (form.password !== form.confirmPassword) {
        toast.error("Passwords do not match");
        setLoading(false);
        return;
      }

      const passwordRegex = /^(?=.*[A-Za-z])(?=.*\d)(?=.*[@$!%*#?&])[A-Za-z\d@$!%*#?&]{8,}$/;

      if (!passwordRegex.test(form.password)) {
        toast.error("Password must be at least 8 characters and contain 1 number and 1 special character");
        setLoading(false);
        return;
      }


      await register(form);
      toast.success("Account created.");
      nav("/");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not register");
    } finally { setLoading(false); }
  };

  return (
    <main className="min-h-[calc(100vh-200px)] grid lg:grid-cols-2" data-testid="register-page">
      <div className="relative hidden lg:block">
        <img src={REGISTER_IMG} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-tr from-primary/80 via-primary/40 to-secondary/40" />
        <div className="relative h-full flex flex-col justify-between p-12 text-white">
          <img src={NIVILABS_LOGO} alt="nivilabs" className="h-16 w-16 object-contain" />
          <div>
            <div className="font-display text-4xl font-bold leading-tight">Join 200+ research labs.</div>
            <p className="text-white/80 mt-3 max-w-md">Create your nivilabs account to save products, submit inquiries, and track quotations.</p>
          </div>
        </div>
      </div>
      <div className="flex items-center justify-center p-6 sm:p-12">
        <form onSubmit={submit} className="w-full max-w-md space-y-4">
          <div>
            <div className="text-xs tracking-[0.2em] uppercase text-primary font-semibold">Create account</div>
            <h1 className="font-display text-3xl font-bold mt-1">Get started</h1>
          </div>
          <Field label="Full name *"><Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="reg-name" /></Field>
          <Field label="Email *"><Input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="reg-email" /></Field>
          <Field label="Password *">
            <div className="relative">
              <Input
                type={showPassword ? "text" : "password"}
                required
                minLength={8}
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                data-testid="reg-password"
              />
              <button
                type="button"
                className="absolute inset-y-0 right-3 inline-flex items-center text-muted-foreground hover:text-foreground"
                onClick={() => setShowPassword((prev) => !prev)}
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </Field>
          <Field label="Confirm Password *">
            <div className="relative">
              <Input
                type={showConfirmPassword ? "text" : "password"}
                required
                value={form.confirmPassword}
                onChange={(e) => setForm({ ...form, confirmPassword: e.target.value })}
                data-testid="reg-confirm-password"
              />
              <button
                type="button"
                className="absolute inset-y-0 right-3 inline-flex items-center text-muted-foreground hover:text-foreground"
                onClick={() => setShowConfirmPassword((prev) => !prev)}
                aria-label={showConfirmPassword ? "Hide confirm password" : "Show confirm password"}
              >
                {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </Field>
          <Field label="Phone *"><Input required type="tel" pattern="[0-9]{10}" maxLength={10} title="Enter a valid 10-digit phone number" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value.replace(/\D/g, "") })} data-testid="reg-phone" /></Field>
          <Field label="Address *"><Input required value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value, })} data-testid="reg-address"/></Field>
          <Field label="Pincode *"><Input required pattern="[0-9]{6}" maxLength={6} title="Enter a valid 6-digit pincode" value={form.pincode} onChange={(e) => setForm({ ...form, pincode: e.target.value.replace(/\D/g, ""),})} data-testid="reg-pincode"/></Field>
          <Field label="Organization *"><Input required value={form.organization} onChange={(e) => setForm({ ...form, organization: e.target.value })} data-testid="reg-org" /></Field>
          <Field label="GST Number (Optional)"><Input value={form.gst} onChange={(e) => setForm({ ...form, gst: e.target.value.toUpperCase(), })} data-testid="reg-gst"/></Field>
          <Button type="submit" disabled={loading} className="w-full h-11 bg-primary hover:bg-primary/90" data-testid="reg-submit">
            {loading ? "Creating…" : "Create account"}
          </Button>
          <div className="text-sm text-center text-muted-foreground">
            Already have an account? <Link to="/login" className="text-primary hover:underline">Sign in</Link>
          </div>
        </form>
      </div>
    </main>
  );
}

const Field = ({ label, children }) => (
  <div className="space-y-1.5"><Label className="text-xs font-medium">{label}</Label>{children}</div>
);
